import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Enums
export enum ReportPeriodType {
  Month = 1,
  Quarter = 2,
  Year = 3,
  Custom = 4
}

export enum PaymentStatusType {
  PaidInFull = 1,
  InInstallments = 2,
  HasDebt = 3
}

// Interfaces - Cash Flow Report
export interface MonthlyExpectedIncomeDto {
  monthOffset: number;
  monthName: string;
  expectedAmount: number;
}

export interface CustomerCashFlowDto {
  clientId: number;
  customerName: string;
  monthlyBenefitAmount: number | null; // null בחיוב ידני לפני ועדה
  commissionPercentage: number;
  totalToCollect: number; // יתרה לאחר הנחה, כולל מע"מ
  hasPaymentPlan: boolean; // בלי תוכנית — אין צפי (אין מנגנון גבייה בלי תוכנית)
  remainingInstallments: number | null; // תשלומים שנותרו בתוכנית
  expectedMonthlyIncome: number | null; // סכום התשלום הבא בתוכנית
  sixMonthsDistribution: MonthlyExpectedIncomeDto[];
}

export interface CashFlowReportDto {
  monthlyActualIncome: number;
  nextMonthExpectedIncome: number;
  threeMonthsExpectedIncome: number;
  clientsWithoutPlanCount: number; // לקוחות עם חוב בלי תוכנית תשלומים
  clientsWithoutPlanTotal: number;
  monthlyForecast: MonthlyExpectedIncomeDto[]; // התפלגות הצפי מהחודש הבא — מחושבת בשרת
  customerDetails: CustomerCashFlowDto[];
}

// Interfaces - Collection and Debt Report
export interface PaymentStatisticsDto {
  customersPaidInFull: number;
  customersInInstallments: number;
  customersWithDebt: number;
  totalOpenDebts: number;
}

export interface CustomerDebtDto {
  clientId: number;
  customerName: string;
  totalChargeWithVat: number; // סה"כ חיוב כולל מע"מ
  totalPaid: number;
  totalDiscount: number; // הנחות כולל מע"מ — חיוב − שולם − הנחות = יתרה
  balance: number; // יתרת חוב לאחר הנחה (כולל מע"מ), זהה למסך החיובים של הלקוח
  lastPaymentDate?: string;
  paymentStatus: PaymentStatusType;
}

export interface CollectionDebtReportDto {
  paymentStatistics: PaymentStatisticsDto;
  customerDetails: CustomerDebtDto[];
}

// Interfaces - Team Performance Report
export interface MonthlySummaryDto {
  totalNewLeads: number;
  totalSignatures: number;
  conversionPercentage: number;
  totalApprovedCases: number;
  totalBenefitAmount: number;
}

export interface TeamPerformanceReportDto {
  monthlySummary: MonthlySummaryDto;
}

// Interfaces - Special Payments Report
export interface SpecialPaymentsReportDto {
  totalPending: number;
  totalPendingAmount: number;
  paidInPeriod: number;
  paidInPeriodAmount: number;
  cancelledInPeriod: number;
  payments: SpecialPaymentReportItemDto[];
}

export interface SpecialPaymentReportItemDto {
  id: number;
  clientId: number;
  clientName: string;
  amount: number;
  description: string;
  status: number;
  statusName: string;
  paymentMethod?: number;
  paymentMethodName?: string;
  paymentDate?: string;
  createdAt: string;
  createdByName: string;
}

// Report Filter
export interface ReportFilterDto {
  dateFrom?: string;
  dateTo?: string;
  periodType: ReportPeriodType;
}

// API Service
class ReportsApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  async getCashFlowReport(filter: ReportFilterDto): Promise<CashFlowReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/cash-flow`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getCollectionDebtReport(filter: ReportFilterDto): Promise<CollectionDebtReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/collection-debt`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getTeamPerformanceReport(filter: ReportFilterDto): Promise<TeamPerformanceReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/team-performance`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getSpecialPaymentsReport(filter: ReportFilterDto): Promise<SpecialPaymentsReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/special-payments`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }
}

export const reportsApi = new ReportsApiService();

// Helper Functions
export const getReportPeriodName = (periodType: ReportPeriodType): string => {
  switch (periodType) {
    case ReportPeriodType.Month:
      return 'חודש';
    case ReportPeriodType.Quarter:
      return 'רבעון';
    case ReportPeriodType.Year:
      return 'שנה';
    case ReportPeriodType.Custom:
      return 'מותאם אישית';
    default:
      return 'חודש';
  }
};

export const getPaymentStatusName = (status: PaymentStatusType): string => {
  switch (status) {
    case PaymentStatusType.PaidInFull:
      return 'שולם במלואו';
    case PaymentStatusType.InInstallments:
      return 'תשלומים';
    case PaymentStatusType.HasDebt:
      return 'יש חוב';
    default:
      return 'לא ידוע';
  }
};

export const getPaymentStatusColor = (status: PaymentStatusType): string => {
  switch (status) {
    case PaymentStatusType.PaidInFull:
      return 'var(--success-color)';
    case PaymentStatusType.InInstallments:
      return 'var(--warning-color)';
    case PaymentStatusType.HasDebt:
      return 'var(--error-color)';
    default:
      return 'var(--text-secondary)';
  }
};

export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatPercentage = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';
